import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { UIWheelSegments } from "../../common_ui_elements/ui_wheel_segments.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import type { DailyTaskRewardDto } from "../../../api/dto/daily_tasks.dto.js";
import { hapticsController, VibrationContext } from "../../../haptics/haptics_controller.js";
import { HapticPattern } from "../../../haptics/haptics_constants.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";
import { IController } from "../../../webgl_2d/core/types/controller_types.js";
import type { RewardFlySource } from "../../reward_fly/reward_fly_to_hud.js";
import { RewardFlySources } from "../../reward_fly/reward_fly_sources.js";

export interface HappyFallResultOverlayParams
{
  rewards: DailyTaskRewardDto[];
  onClaim: ( sources: RewardFlySource[] ) => void;
}

interface Box { x: number; y: number; w: number; h: number; }

const DESIGN_W = 1080;
const DESIGN_H = 1920;

export class HappyFallResultOverlay extends AbstractOverlay
{
  private static readonly BG_FADE_MS = 140;
  private static readonly BANNER_SLIDE_MS = 190;
  private static readonly LOGO_APPEAR_MS = 140;
  private static readonly LOGO_APPEAR_SCALE = 0.6;
  private static readonly CONTENT_DELAY_MS = 30;
  private static readonly CONTENT_APPEAR_MS = 130;

  private static readonly LOGO_W = 560;
  private static readonly LOGO_H = 260;
  private static readonly LOGO_Y = 162;

  private static readonly BANNER_HALF_W = 517;
  private static readonly BANNER_H = 275;
  private static readonly BANNER_Y = 195;
  private static readonly BANNER_SEAM_OVERLAP = 4;

  private static readonly LABEL_W = 700;
  private static readonly LABEL_H = 70;
  private static readonly LABEL_FONT = 50;

  private static readonly REWARD_LABEL_Y = 545;
  private static readonly REWARD_Y = 680;
  private static readonly REWARD_SIZE = 200;
  private static readonly REWARD_GAP = 40;
  private static readonly REWARD_ROW_MAX_W = 960;
  private static readonly REWARD_AMOUNT_OFFSET = 155;
  private static readonly REWARD_AMOUNT_FONT = 52;
  private static readonly REWARD_GLOW_RADIUS = 115;
  private static readonly REWARD_GLOW_ROTATION_SPEED = 0.45;

  private static readonly NO_PRIZES_RACCOON_SIZE = 750;
  private static readonly NO_PRIZES_RACCOON_Y = 620;

  private static readonly BUTTON_W = 740;
  private static readonly BUTTON_H = 240;
  private static readonly BUTTON_Y = 1500;
  private static readonly BUTTON_FONT = 74;

  private readonly appear_start = performance.now();
  private readonly content: UIElement[] = [];
  private readonly banner_halves: UIImage[] = [];
  private readonly reward_glows: IController[] = [];
  private readonly reward_icons: { reward: DailyTaskRewardDto; icon: UIImage }[] = [];
  private logo!: UIImage;
  private interactive_at = 0;
  private closed = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: HappyFallResultOverlayParams,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    this.createBanner();
    this.createLogo();
    this.createRewards();
    this.createClaimButton();
    this.startAppearAnimation();

    this.interactive_at = this.contentAt() + HappyFallResultOverlay.CONTENT_APPEAR_MS * 0.6;
  }

  private get result(): HappyFallResultOverlayParams
  {
    return this.params as HappyFallResultOverlayParams;
  }

  private bannerAt(): number
  {
    return this.appear_start + HappyFallResultOverlay.BG_FADE_MS;
  }

  private logoAt(): number
  {
    return this.bannerAt() + HappyFallResultOverlay.BANNER_SLIDE_MS;
  }

  private contentAt(): number
  {
    return this.logoAt() + HappyFallResultOverlay.LOGO_APPEAR_MS + HappyFallResultOverlay.CONTENT_DELAY_MS;
  }

  private addContent( element: UIElement ): void
  {
    this.addChild( element );
    this.content.push( element );
  }

  private box( dx: number, dy: number, dw: number, dh: number ): Box
  {
    const w = LayoutScaler.widthFromDesign( dw );
    const h = LayoutScaler.heightFromDesign( dh );
    const cx = this.rect.getW2() + LayoutScaler.widthFromDesign( dx + dw / 2 - DESIGN_W / 2 );
    const cy = this.rect.getH2() + LayoutScaler.heightFromDesign( dy + dh / 2 - DESIGN_H / 2 );

    return { x: cx - w / 2, y: cy - h / 2, w, h };
  }

  private centered( dy: number, dw: number, dh: number ): Box
  {
    const dx = ( DESIGN_W - dw ) / 2;

    return this.box( dx, dy, dw, dh );
  }

  private createBanner(): void
  {
    const banner_w = HappyFallResultOverlay.BANNER_HALF_W * 2;
    const b = this.centered( HappyFallResultOverlay.BANNER_Y, banner_w, HappyFallResultOverlay.BANNER_H );
    const half_w = b.w / 2;
    const overlap = LayoutScaler.widthFromDesign( HappyFallResultOverlay.BANNER_SEAM_OVERLAP );
    const right_x = b.x + half_w - overlap;

    const left = new UIImage( this.ctx, b.x, b.y, half_w, b.h, ImgID.UI_ConveyorResultBanner );
    left.setClickable( false );
    this.addChild( left );

    const right = new UIImage( this.ctx, right_x, b.y, half_w, b.h, ImgID.UI_ConveyorResultBanner );
    right.setFlipX( true );
    right.setClickable( false );
    this.addChild( right );

    this.banner_halves.push( left, right );
  }

  private createLogo(): void
  {
    const b = this.centered( HappyFallResultOverlay.LOGO_Y, HappyFallResultOverlay.LOGO_W, HappyFallResultOverlay.LOGO_H );

    this.logo = new UIImage( this.ctx, b.x, b.y, b.w, b.h, ImgID.UI_HappyFallLogo );
    this.logo.setClickable( false );
    this.addChild( this.logo );
  }

  private createRewardLabel( localization_key: string ): void
  {
    const b = this.centered( HappyFallResultOverlay.REWARD_LABEL_Y, HappyFallResultOverlay.LABEL_W, HappyFallResultOverlay.LABEL_H );

    this.addContent( new UITextElement( this.ctx, b.x, b.y, b.w, b.h, {
      localization_key,
      font_size: LayoutScaler.textSizeFromDesign( HappyFallResultOverlay.LABEL_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      shadow_offset_x: 0,
      shadow_offset_y: 0,
      outline_color: ColorScheme.booster_dialog_quantity_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } ) );
  }

  private createRewards(): void
  {
    const rewards = this.result.rewards;
    if ( rewards.length === 0 )
    {
      this.createNoPrizes();
      return;
    }

    this.createRewardLabel( "CONVEYOR.REWARD_LABEL" );

    const gap = HappyFallResultOverlay.REWARD_GAP;
    const fit = ( HappyFallResultOverlay.REWARD_ROW_MAX_W - ( rewards.length - 1 ) * gap ) / rewards.length;
    const size = Math.min( HappyFallResultOverlay.REWARD_SIZE, fit );
    const row_w = rewards.length * size + ( rewards.length - 1 ) * gap;
    const row_x = ( DESIGN_W - row_w ) / 2;

    rewards.forEach( ( reward, i ) => this.createReward( reward, row_x + i * ( size + gap ), size ) );
  }

  private createNoPrizes(): void
  {
    this.createRewardLabel( "HAPPY_FALL.NO_PRIZES" );

    const size = HappyFallResultOverlay.NO_PRIZES_RACCOON_SIZE;
    const b = this.centered( HappyFallResultOverlay.NO_PRIZES_RACCOON_Y, size, size );

    const raccoon = new UIImage( this.ctx, b.x, b.y, b.w, b.h, ImgID.UI_HappyFallRaccoonNoPrizes );
    raccoon.setClickable( false );
    this.addContent( raccoon );
  }

  private createReward( reward: DailyTaskRewardDto, dx: number, size: number ): void
  {
    const icon_box = this.box( dx, HappyFallResultOverlay.REWARD_Y, size, size );
    const overlay_absolute = this.getAbsolutePosition();
    const glow_x = overlay_absolute.x + icon_box.x + icon_box.w / 2;
    const glow_y = overlay_absolute.y + icon_box.y + icon_box.h / 2;
    const glow_radius = LayoutScaler.widthFromDesign( HappyFallResultOverlay.REWARD_GLOW_RADIUS * size / HappyFallResultOverlay.REWARD_SIZE );

    this.reward_glows.push( Vfx2DManager.addCoinCounterGlow(
      new Vector2( glow_x, glow_y ),
      glow_radius,
      this.contentAt(),
      0,
      true,
      HappyFallResultOverlay.CONTENT_APPEAR_MS,
      true,
      HappyFallResultOverlay.REWARD_GLOW_ROTATION_SPEED
    ) );

    const icon_id = UIWheelSegments.rewardIcon( reward );
    if ( icon_id !== null )
    {
      const icon = new UIImage( this.ctx, icon_box.x, icon_box.y, icon_box.w, icon_box.h, icon_id );
      icon.setClickable( false );
      this.addContent( icon );
      this.reward_icons.push( { reward, icon } );
    }

    const amount_dx = dx + size / 2 - HappyFallResultOverlay.LABEL_W / 2;
    const amount_dy = HappyFallResultOverlay.REWARD_Y + size * HappyFallResultOverlay.REWARD_AMOUNT_OFFSET / HappyFallResultOverlay.REWARD_SIZE;
    const amount = this.box( amount_dx, amount_dy, HappyFallResultOverlay.LABEL_W, HappyFallResultOverlay.LABEL_H );

    this.addContent( new UITextElement( this.ctx, amount.x, amount.y, amount.w, amount.h, {
      text: UIWheelSegments.rewardLabel( reward ),
      font_size: LayoutScaler.textSizeFromDesign( HappyFallResultOverlay.REWARD_AMOUNT_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.friends_title_outline,
      auto_wrap: false,
      shadow_offset_x: 0,
      shadow_offset_y: 0,
    } ) );
  }

  private createClaimButton(): void
  {
    const b = this.centered( HappyFallResultOverlay.BUTTON_Y, HappyFallResultOverlay.BUTTON_W, HappyFallResultOverlay.BUTTON_H );
    const localization_key = this.result.rewards.length > 0 ? "CLAIM" : "OK";

    const button = new UINineSliceElement( this.ctx, b.x, b.y, b.w, b.h, ImgID.UI_GreenFrameBtn, "green_frame_btn", undefined, undefined, true );

    button.addChild( new UITextElement( this.ctx, 0, 0, b.w, b.h, {
      localization_key,
      font_size: LayoutScaler.textSizeFromDesign( HappyFallResultOverlay.BUTTON_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      auto_wrap: false,
      scale_to_width: true,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      using_y_offset: false,
      outline_color: ColorScheme.menu_button_outline,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: ColorScheme.conveyor_result_shadow,
      shadow_offset_x: 0,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
    } ), true );

    button.setOnClick( () => this.claim() );
    this.addContent( button );
  }

  private claim(): void
  {
    if ( this.closed )
      return;

    this.closed = true;
    void hapticsController.vibrate( HapticPattern.LIGHT, VibrationContext.GENERAL );

    const sources = this.reward_icons.map( ( { reward, icon } ) => RewardFlySources.fromElement( reward, icon ) );
    try
    {
      this.result.onClaim( sources );
    }
    catch ( error )
    {
      console.error( "[HappyFallResultOverlay] Claim failed:", error );
    }
    finally
    {
      OverlayManager.closeOverlay();
    }
  }

  private startAppearAnimation(): void
  {
    OverlayManager.fadeInBackground( this.appear_start - performance.now(), HappyFallResultOverlay.BG_FADE_MS );
    this.animateBanner();
    this.animateLogo();
    this.animateContent();
  }

  private animateBanner(): void
  {
    const shift = this.rect.getW();

    this.banner_halves.forEach( ( half, i ) =>
    {
      const final_pos = half.pixelPosImportant().clone();
      const side = i === 0 ? -1 : 1;
      const start_x = final_pos.x + side * shift;
      const start_pos = new Vector2( start_x, final_pos.y );

      half.setPixelPos( start_pos, true );
      half.addTransform( new TransformStraightMove( half, true, start_pos, final_pos, this.bannerAt(), HappyFallResultOverlay.BANNER_SLIDE_MS, { easing: Easing.EaseOutQuad } ) );
    } );
  }

  private animateLogo(): void
  {
    const logo = this.logo;
    const start_scale = new Vector2( HappyFallResultOverlay.LOGO_APPEAR_SCALE, HappyFallResultOverlay.LOGO_APPEAR_SCALE );

    logo.setScale( start_scale );
    logo.setOpacity( 0 );

    logo.addTransform( new TransformScale( logo, true, start_scale, new Vector2( 1, 1 ), this.logoAt(), HappyFallResultOverlay.LOGO_APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
    logo.addTransform( new TransformOpacity( logo, true, 0, 1, this.logoAt(), HappyFallResultOverlay.LOGO_APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
  }

  private animateContent(): void
  {
    for ( const element of this.content )
    {
      element.setOpacity( 0 );
      element.addTransform( new TransformOpacity( element, true, 0, 1, this.contentAt(), HappyFallResultOverlay.CONTENT_APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
    }
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    if ( now < this.interactive_at || this.closed )
      return true;

    return super.onTouchStart( p, now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public override onOverlayWillClose(): void
  {
    for ( const glow of this.reward_glows )
      glow.stop();

    this.reward_glows.length = 0;
  }

  public name(): string
  {
    return "HappyFallResultOverlay";
  }
}
